import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { warrantyApi } from '../api/warrantyApi';

// Re-export des hooks partagés (lecture)
export { useWarranties, useAvailableWarranties } from '@/features/warranties/hooks/useWarranties';

// Re-export des hooks partagés (mutations liste + création)
export {
  useActivateWarranty,
  useDeactivateWarranty,
  useUpdateWarranty,
  useMoveWarranty,
  useDuplicateWarranty,
  useCreateWarranty,
  usePropagateWarrantyDiagnostic,
} from '@/features/warranties/hooks/useWarrantyMutations';

// ==================== QUERIES locales (lecture admin-only) ====================

/**
 * Détail d'une garantie
 */
export const useWarranty = (id: string | undefined) => {
  return useQuery({
    queryKey: ['warranty', id],
    queryFn: () => warrantyApi.getWarranty(id!),
    enabled: !!id,
    staleTime: 30000,
  });
};

/**
 * Récupérer une garantie par code
 */
export const useWarrantyByCode = (code: string | undefined) => {
  return useQuery({
    queryKey: ['warranty-by-code', code],
    queryFn: () => warrantyApi.getWarrantyByCode(code!),
    enabled: !!code,
    staleTime: 30000,
  });
};

/**
 * Garanties d'une société
 */
export const useWarrantiesBySociety = (societyId: string | undefined) => {
  return useQuery({
    queryKey: ['warranties-by-society', societyId],
    queryFn: () => warrantyApi.getWarrantiesBySociety(societyId!),
    enabled: !!societyId,
    staleTime: 30000,
  });
};

/**
 * Garanties héritées d'une société
 */
export const useInheritedWarranties = (societyId: string | undefined) => {
  return useQuery({
    queryKey: ['inherited-warranties', societyId],
    queryFn: () => warrantyApi.getInheritedWarranties(societyId!),
    enabled: !!societyId,
    staleTime: 30000,
  });
};

/**
 * Audit logs d'une garantie
 */
export const useWarrantyAuditLogs = (id: string | undefined, limit = 50) => {
  return useQuery({
    queryKey: ['warranty-audit-logs', id, limit],
    queryFn: () => warrantyApi.getWarrantyAuditLogs(id!, limit),
    enabled: !!id,
    staleTime: 10000,
  });
};

/**
 * Statistiques d'une garantie
 */
export const useWarrantyStats = (id: string | undefined) => {
  return useQuery({
    queryKey: ['warranty-stats', id],
    queryFn: () => warrantyApi.getWarrantyStats(id!),
    enabled: !!id,
    staleTime: 60000,
  });
};

// ==================== MUTATIONS locales (écriture) ====================

export const useDeleteWarranty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => warrantyApi.deleteWarranty(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      queryClient.invalidateQueries({ queryKey: ['warranty'] });
      queryClient.invalidateQueries({ queryKey: ['warranties-by-society'] });
      queryClient.invalidateQueries({ queryKey: ['available-warranties'] });
    },
  });
};


export const useBroadcastWarranty = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, broadcast }: { id: string; broadcast: boolean }) =>
      warrantyApi.broadcastWarranty(id, broadcast),
    onSuccess: (data, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['warranty', id] });
      queryClient.invalidateQueries({ queryKey: ['warranties'] });
      queryClient.invalidateQueries({ queryKey: ['warranties-by-society', data.societyId] });
      queryClient.invalidateQueries({ queryKey: ['available-warranties'] });
      queryClient.invalidateQueries({ queryKey: ['inherited-warranties'] });
    },
  });
};


export const useAddParts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ warrantyId, partIds }: { warrantyId: string; partIds: number[] }) =>
      warrantyApi.addParts(warrantyId, partIds),
    onSuccess: (_, { warrantyId }) => {
      queryClient.invalidateQueries({ queryKey: ['warranty', warrantyId] });
      queryClient.invalidateQueries({ queryKey: ['warranty-pdf', warrantyId] });
    },
  });
};

export const useRemoveParts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ warrantyId, partIds }: { warrantyId: string; partIds: number[] }) =>
      warrantyApi.removeParts(warrantyId, partIds),
    onSuccess: (_, { warrantyId }) => {
      queryClient.invalidateQueries({ queryKey: ['warranty', warrantyId] });
      queryClient.invalidateQueries({ queryKey: ['warranty-pdf', warrantyId] });
    },
  });
};

export const useReplaceParts = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ warrantyId, partIds }: { warrantyId: string; partIds: number[] }) =>
      warrantyApi.replaceParts(warrantyId, partIds),
    onSuccess: (_, { warrantyId }) => {
      queryClient.invalidateQueries({ queryKey: ['warranty', warrantyId] });
      queryClient.invalidateQueries({ queryKey: ['warranty-pdf', warrantyId] });
    },
  });
};

export const useValidateWarrantyCode = () => {
  return useMutation({
    mutationFn: ({ code, excludeId }: { code: string; excludeId?: string }) =>
      warrantyApi.validateCode(code, excludeId),
  });
};

export const useGenerateWarrantyCode = () => {
  return useMutation({
    mutationFn: (societyCode: string) => warrantyApi.generateCode(societyCode),
  });
};

export const useDownloadWarrantyPdf = () => {
  return useMutation({
    mutationFn: async (uuid: string) => {
      const blob = await warrantyApi.downloadWarrantyPdf(uuid);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `conditions_garantie_${uuid.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
  });
};

export const useProvisionsPreview = (societyId: string | undefined) => {
  return useQuery({
    queryKey: ['provisions-preview', societyId],
    queryFn: () => warrantyApi.getProvisionsPreview(societyId!),
    enabled: false,
    staleTime: 0,
  });
};

export const useUpdateWarrantyProvisions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (societyId: string) => warrantyApi.updateProvisionsForSociety(societyId),
    onSuccess: (_data, societyId) => {
      queryClient.invalidateQueries({ queryKey: ['warranties-by-society', societyId] });
      queryClient.invalidateQueries({ queryKey: ['available-warranties', societyId] });
    },
  });
};

/**
 * Clés React Query dont les données dépendent de WARRANTY_PROVISION côté
 * contrat. Une mise à jour des provisions modifie des contrats, pas la
 * garantie : sans ces invalidations l'écran afficherait le compteur de
 * succès tout en continuant de servir les anciennes valeurs depuis le cache.
 */
const PROVISION_DEPENDENT_QUERY_KEYS = [
  ['contract'],
  ['contracts'],
  ['dashboard'],
  ['society-dashboard'],
  ['society-dashboard-v2'],
  ['external-user-dashboard'],
  ['financial-compiled'],
  ['financial-summary'],
  ['provision-export'],
  ['comprehensive-analytics'],
];

export const useUpdateWarrantyProvisionsById = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (warrantyId: string) => warrantyApi.updateProvisionsForWarranty(warrantyId),
    onSuccess: (_, warrantyId) => {
      queryClient.invalidateQueries({ queryKey: ['warranty', warrantyId] });
      queryClient.invalidateQueries({ queryKey: ['warranty-pdf', warrantyId] });
      PROVISION_DEPENDENT_QUERY_KEYS.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey });
      });
    },
  });
};
